export type Candle = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
};

export type MarketStatus = {
  source: string;
  fetchedAt: number | null;
  connected: boolean;
  stale: boolean;
  error: string | null;
};

export const BINANCE_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT'];
export const GLOBAL_SYMBOLS = ['XAUUSD', 'XAGUSD', 'EURUSD', 'SPX500', 'NAS100', 'US30'];
export const SYMBOLS = [...BINANCE_SYMBOLS, ...GLOBAL_SYMBOLS];
export const TIMEFRAMES = [
  { value: '1m', label: '۱دقیقه' },
  { value: '5m', label: '۵دقیقه' },
  { value: '15m', label: '۱۵دقیقه' },
  { value: '1h', label: '۱ساعت' },
  { value: '4h', label: '۴ساعت' },
  { value: '1d', label: '۱روز' },
];

const MARKET_API = '/api/v1/market/candles';

function marketSocketUrl(symbol: string, interval: string): string {
  const url = new URL(`/ws/market/${symbol}/${interval}`, window.location.origin);
  url.protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

export async function fetchCandles(symbol: string, interval: string, limit = 160): Promise<Candle[]> {
  const response = await fetch(`${MARKET_API}?symbol=${symbol}&interval=${interval}&limit=${limit}`, { credentials: 'include' });
  if (!response.ok) throw new Error(`Binance HTTP ${response.status}`);
  const payload = await response.json() as { candles?: Candle[] };
  return (payload.candles ?? []).map((candle) => ({
    time: Number(candle.time),
    open: Number(candle.open),
    high: Number(candle.high),
    low: Number(candle.low),
    close: Number(candle.close),
    volume: Number(candle.volume),
  }));
}

export function openMarketSocket(symbol: string, interval: string, onCandle: (candle: Candle) => void, onState: (connected: boolean, error?: string) => void) {
  if (!BINANCE_SYMBOLS.includes(symbol.toUpperCase())) {
    onState(true);
    return () => undefined;
  }
  let socket: WebSocket | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  let retryDelay = 1000;
  const connect = () => {
    if (stopped) return;
    onState(false, 'در حال اتصال مجدد به بازار…');
    socket = new WebSocket(marketSocketUrl(symbol.toLowerCase(), interval));
    socket.onopen = () => { retryDelay = 1000; onState(true); };
    socket.onerror = () => onState(false, 'اتصال WebSocket بازار با خطا مواجه شد.');
    socket.onclose = () => {
      if (stopped) return;
      onState(false, 'اتصال بازار قطع شد؛ تلاش مجدد خودکار…');
      retryTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 15000);
    };
    socket.onmessage = (message) => {
      try {
        const payload = JSON.parse(message.data) as { k: { t: number; o: string; h: string; l: string; c: string; v: string } };
        if (!payload.k) return;
        onCandle({ time: payload.k.t / 1000, open: Number(payload.k.o), high: Number(payload.k.h), low: Number(payload.k.l), close: Number(payload.k.c), volume: Number(payload.k.v) });
      } catch {
        onState(false, 'پیام بازار قابل پردازش نبود.');
      }
    };
  };
  connect();
  return () => { stopped = true; if (retryTimer) clearTimeout(retryTimer); socket?.close(); };
}

export function sma(values: number[], period: number): (number | null)[] {
  return values.map((_, index) => index < period - 1 ? null : values.slice(index - period + 1, index + 1).reduce((sum, value) => sum + value, 0) / period);
}

export function ema(values: number[], period: number): (number | null)[] {
  const result: (number | null)[] = Array(values.length).fill(null);
  if (values.length < period) return result;
  let previous = values.slice(0, period).reduce((sum, value) => sum + value, 0) / period;
  result[period - 1] = previous;
  const multiplier = 2 / (period + 1);
  for (let index = period; index < values.length; index += 1) {
    previous = (values[index] - previous) * multiplier + previous;
    result[index] = previous;
  }
  return result;
}

export function rsi(values: number[], period = 14): (number | null)[] {
  const result: (number | null)[] = Array(values.length).fill(null);
  if (values.length <= period) return result;
  let gains = 0;
  let losses = 0;
  for (let index = 1; index <= period; index += 1) {
    const change = values[index] - values[index - 1];
    gains += Math.max(change, 0);
    losses += Math.max(-change, 0);
  }
  let averageGain = gains / period;
  let averageLoss = losses / period;
  result[period] = averageLoss === 0 ? 100 : 100 - (100 / (1 + averageGain / averageLoss));
  for (let index = period + 1; index < values.length; index += 1) {
    const change = values[index] - values[index - 1];
    averageGain = (averageGain * (period - 1) + Math.max(change, 0)) / period;
    averageLoss = (averageLoss * (period - 1) + Math.max(-change, 0)) / period;
    result[index] = averageLoss === 0 ? 100 : 100 - (100 / (1 + averageGain / averageLoss));
  }
  return result;
}

export function atr(candles: Candle[], period = 14): (number | null)[] {
  const trueRanges = candles.map((candle, index) => {
    if (index === 0) return candle.high - candle.low;
    const previous = candles[index - 1].close;
    return Math.max(candle.high - candle.low, Math.abs(candle.high - previous), Math.abs(candle.low - previous));
  });
  return sma(trueRanges, period);
}

export function bollinger(values: number[], period = 20, multiplier = 2): { upper: (number | null)[]; lower: (number | null)[] } {
  const upper: (number | null)[] = Array(values.length).fill(null);
  const lower: (number | null)[] = Array(values.length).fill(null);
  for (let index = period - 1; index < values.length; index += 1) {
    const window = values.slice(index - period + 1, index + 1);
    const mean = window.reduce((sum, value) => sum + value, 0) / period;
    const deviation = Math.sqrt(window.reduce((sum, value) => sum + (value - mean) ** 2, 0) / period);
    upper[index] = mean + deviation * multiplier;
    lower[index] = mean - deviation * multiplier;
  }
  return { upper, lower };
}

export function formatPrice(value: number): string {
  if (value >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (value >= 1) return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return value.toLocaleString('en-US', { maximumFractionDigits: 6 });
}
